/**
 * ChatSettingsPanel — Notification/preference toggles for the chat widget
 *
 * 340px panel with toggles: Notification sound, Desktop notifications, Message preview.
 * Settings stored in localStorage ('vidnight_chat_settings').
 */

import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import { useChatSettings } from '../utils/chatSettings';
import { sound } from '@/utils/sounds';

interface ChatSettingsPanelProps {
  onClose: () => void;
}

export const ChatSettingsPanel = memo(function ChatSettingsPanel({ onClose }: ChatSettingsPanelProps) {
  const { t } = useTranslation();
  const { settings, updateSetting } = useChatSettings();

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 8 }}
      className="flex w-[340px] flex-col overflow-hidden rounded-t-xl border shadow-lg"
      style={{ borderColor: 'var(--user-page-card-border)', backgroundColor: 'var(--user-page-card-bg)' }}
    >
      {/* Header */}
      <div className="flex h-12 flex-shrink-0 items-center justify-between border-b px-4" style={{ borderColor: 'var(--user-page-card-border)' }}>
        <span className="text-sm font-semibold" style={{ color: 'var(--user-page-heading)' }}>
          {t('messaging.settings.title')}
        </span>
        <button
          type="button"
          onClick={onClose}
          className="flex h-6 w-6 items-center justify-center rounded-full transition-colors hover:bg-slate-100"
          style={{ color: 'var(--user-page-text)' }}
        >
          <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      {/* Settings */}
      <div className="flex flex-col gap-1 p-4">
        <ToggleRow
          label={t('messaging.settings.notificationSound')}
          checked={settings.notificationSound}
          onChange={(v) => updateSetting('notificationSound', v)}
        />
        <ToggleRow
          label={t('messaging.settings.chatVibration')}
          checked={settings.chatVibration}
          onChange={(v) => updateSetting('chatVibration', v)}
        />
        <ToggleRow
          label={t('messaging.settings.desktopNotifications')}
          checked={settings.desktopNotifications}
          onChange={(v) => updateSetting('desktopNotifications', v)}
        />
        <ToggleRow
          label={t('messaging.settings.messagePreview')}
          checked={settings.messagePreview}
          onChange={(v) => updateSetting('messagePreview', v)}
        />
      </div>
    </motion.div>
  );
});

function ToggleRow({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  const toggle = useCallback(() => { sound.tick(); onChange(!checked); }, [checked, onChange]);

  return (
    <button
      type="button"
      onClick={toggle}
      className="flex items-center justify-between rounded-lg px-2 py-2.5 transition-colors hover:bg-slate-50"
    >
      <span className="text-sm" style={{ color: 'var(--user-page-heading)' }}>{label}</span>
      <div
        className="relative h-5 w-9 rounded-full transition-colors"
        style={{ backgroundColor: checked ? 'var(--user-page-heading)' : 'var(--user-page-card-border)' }}
      >
        <div
          className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
            checked ? 'translate-x-4 rtl:-translate-x-4' : 'translate-x-0.5 rtl:-translate-x-0.5'
          }`}
        />
      </div>
    </button>
  );
}
